/**
 * fontResolve — resolve a TEXT style → renderable font for the PDF backend.
 *
 * Given a `text-run`'s style name (and the document text-style table), this
 * decides whether the font is a TTF (embed via fontkit → searchable) or an SHX
 * (stroke geometry via the shx-parser) and obtains the raw bytes:
 *
 *   1. **Runtime `FontRegistry`** — the user's loaded fonts. A registered SHX
 *      keeps its `bytes`; a registered TTF keeps its bytes on `ttf.bytes`
 *      (`LoadedTtf`). We try the resolved font NAME, then the style NAME.
 *   2. **Bundled fallback** — when the registry has no match (the common case
 *      in headless tests / the bench, where the registry is empty):
 *        - TTF → `gost_common.ttf` (CAD/Cyrillic default for title blocks).
 *        - SHX name with no registered SHX → substitute the bundled TTF so the
 *          glyphs still render (searchable), and flag `substituted: true` so
 *          the caller can note the loss of SHX centerline fidelity.
 *
 * Byte loading works in BOTH the browser (Vite `?url` import → fetch) and node
 * tests (fs.readFileSync) via the {@link BundledFontLoader} abstraction, whose
 * default picks the environment. Callers (tests, the bench) may inject their
 * own loader so resolution is fully deterministic and offline.
 *
 * This module is pure w.r.t. PDF emission — it returns bytes + kind, never
 * touches pdf-lib. The backend (`exportLayoutPdf`) owns embedding / stroking.
 */

import type { TextStyle } from "@dxf-ai-editor/drawing-model";

import { FontRegistry } from "../../fonts/fontRegistry";
import { isShxFontName } from "../../scene3d/text/fontRegistry";

/** Font rendering kind decided for a run. */
export type FontKind = "ttf" | "shx";

/** A fully resolved font ready for the backend to embed / stroke. */
export interface ResolvedFont {
  kind: FontKind;
  /** The logical font name resolved from the style (for caching / debug). */
  name: string;
  /** Raw font bytes (TTF/OTF or SHX). */
  bytes: Uint8Array;
  /** Whether a bundled fallback was used (no runtime registry match). */
  fallback: boolean;
  /**
   * True when an SHX name had no SHX bytes available and we substituted the
   * bundled TTF (centerline-stroke fidelity is lost; the caller may note it).
   */
  substituted: boolean;
}

/**
 * Decide a font's logical name + kind from a text style (or a bare name).
 *
 * The font name precedence mirrors AutoCAD: the style's `fontFile` (e.g.
 * `romans.shx`, `arial.ttf`) is authoritative; if absent we fall back to the
 * style's `fontName`, then the style name itself. SHX-vs-TTF is decided by
 * {@link isShxFontName} (suffix or known SHX family root).
 */
export function resolveFontNameAndKind(style: TextStyle | undefined): {
  name: string;
  kind: FontKind;
} {
  const raw =
    (style?.fontFile && style.fontFile.trim()) ||
    (style?.fontName && style.fontName.trim()) ||
    (style?.name && style.name.trim()) ||
    "txt"; // AutoCAD default SHX
  return { name: raw, kind: isShxFontName(raw) ? "shx" : "ttf" };
}

/* ── Bundled byte loader (browser + node) ───────────────────────────── */

/** Identifiers for the two bundled fonts shipped with the app. */
export type BundledFontId = "gost_common" | "roboto";

/**
 * Loads the raw bytes of a bundled fallback font. The default implementation
 * picks the environment (fs in node, fetch in the browser). Tests / the bench
 * inject a deterministic loader.
 */
export interface BundledFontLoader {
  load(id: BundledFontId): Promise<Uint8Array>;
}

/** Path of a bundled font relative to the worktree root (node-fs loader). */
const BUNDLED_FS_PATHS: Record<BundledFontId, string> = {
  gost_common: "apps/web/src/app/fonts/fixtures/gost_common.ttf",
  roboto: "apps/web/public/fonts/sources/Roboto-Regular.ttf",
};

/**
 * Browser URL of a bundled font. Computed lazily (inside the loader) so module
 * import never evaluates `import.meta.url` in environments where it is not a
 * valid base (some test runners). `gost_common` lives under `src/` (a fixture)
 * — resolved relative to this module so the bundler emits/serves it; Roboto is
 * a public asset served from `/fonts/sources`.
 */
function bundledUrl(id: BundledFontId): string {
  if (id === "roboto") return "/fonts/sources/Roboto-Regular.ttf";
  return new URL("../../fonts/fixtures/gost_common.ttf", import.meta.url).href;
}

/**
 * Whether we are running under node with real fs access and NO browser fetch
 * target. We treat the presence of a usable `fetch` + `document` (or
 * `window`) as the browser path; otherwise the fs path. In a Vite browser
 * build `process` is shimmed but has no `versions.node`, so the fs branch is
 * never taken there.
 */
function isNodeFsEnv(): boolean {
  return (
    typeof process !== "undefined" &&
    process.versions != null &&
    process.versions.node != null &&
    typeof (globalThis as { window?: unknown }).window === "undefined"
  );
}

/**
 * Load a bundled font's bytes from the filesystem (node-only). The `node:fs` /
 * `node:path` specifiers are built dynamically so Vite's static analyser never
 * tries to resolve them into the browser bundle (which would emit an
 * "externalized for browser compatibility" warning). This path is only ever
 * reached under {@link isNodeFsEnv} (vitest / the bench), never in the browser.
 */
async function loadBundledFromFs(id: BundledFontId): Promise<Uint8Array> {
  // Indirect specifier — opaque to the bundler's static import scan.
  const fsSpec = "node:" + "fs";
  const pathSpec = "node:" + "path";
  const { readFileSync } = (await import(/* @vite-ignore */ fsSpec)) as typeof import("node:fs");
  const { resolve } = (await import(/* @vite-ignore */ pathSpec)) as typeof import("node:path");
  const abs = resolve(process.cwd(), BUNDLED_FS_PATHS[id]);
  return new Uint8Array(readFileSync(abs));
}

/** The default environment-aware bundled font loader. */
export const defaultBundledFontLoader: BundledFontLoader = {
  async load(id: BundledFontId): Promise<Uint8Array> {
    if (isNodeFsEnv()) {
      // vitest / bench run from the repo root → read the fixture directly.
      return loadBundledFromFs(id);
    }
    const res = await fetch(bundledUrl(id));
    if (!res.ok) {
      throw new Error(
        `[fontResolve] failed to fetch bundled font '${id}': ${res.status}`,
      );
    }
    return new Uint8Array(await res.arrayBuffer());
  },
};

/* ── Runtime registry bytes ─────────────────────────────────────────── */

/**
 * Try to obtain raw font bytes from the runtime {@link FontRegistry} by name.
 * Returns the bytes + the entry kind, or null when not registered. TTF entries
 * keep their bytes on `ttf.bytes` (LoadedTtf); SHX entries keep `bytes`.
 */
function registryBytes(
  name: string,
): { bytes: Uint8Array; kind: FontKind } | null {
  const entry = FontRegistry.get(name);
  if (!entry) return null;
  if (entry.kind === "shx") {
    return { bytes: entry.bytes, kind: "shx" };
  }
  // TTF: LoadedTtf always carries the original bytes for re-export.
  return { bytes: entry.ttf.bytes, kind: "ttf" };
}

/* ── Resolution ─────────────────────────────────────────────────────── */

/** Options for {@link resolveFont}. */
export interface ResolveFontOptions {
  /** Override the bundled loader (tests / bench). */
  bundledLoader?: BundledFontLoader;
  /**
   * Inject explicit bytes by font name (tests). When a name matches a key here
   * we use those bytes directly with the kind decided by {@link isShxFontName}
   * — bypasses both the registry and the bundled loader for determinism.
   */
  injectedBytes?: Record<string, { bytes: Uint8Array; kind: FontKind }>;
}

/**
 * Resolve a text style to renderable font bytes + kind.
 *
 * Precedence: injected bytes → runtime FontRegistry (by resolved name, then by
 * style name) → bundled fallback. SHX names with no SHX bytes substitute the
 * bundled TTF (flagged) so glyphs still render.
 */
export async function resolveFont(
  style: TextStyle | undefined,
  options: ResolveFontOptions = {},
): Promise<ResolvedFont> {
  const loader = options.bundledLoader ?? defaultBundledFontLoader;
  const { name, kind } = resolveFontNameAndKind(style);

  // (0) Injected bytes (tests) — keyed by resolved name OR style name.
  const injected = options.injectedBytes;
  if (injected) {
    const hit = injected[name] ?? (style ? injected[style.name] : undefined);
    if (hit) {
      return {
        kind: hit.kind,
        name,
        bytes: hit.bytes,
        fallback: false,
        substituted: false,
      };
    }
  }

  // (1) Runtime registry — try the resolved name, then the style name.
  const fromRegistry =
    registryBytes(name) ?? (style ? registryBytes(style.name) : null);
  if (fromRegistry) {
    return {
      kind: fromRegistry.kind,
      name,
      bytes: fromRegistry.bytes,
      fallback: false,
      substituted: false,
    };
  }

  // (2) Bundled fallback.
  if (kind === "shx") {
    // No registered SHX for this name → substitute the bundled CAD TTF so the
    // glyphs (incl. Cyrillic) still render. SHX centerline fidelity is lost;
    // flag it so the caller can warn / count.
    const bytes = await loader.load("gost_common");
    return { kind: "ttf", name, bytes, fallback: true, substituted: true };
  }
  // TTF fallback → the bundled CAD/Cyrillic default (gost_common).
  const bytes = await loader.load("gost_common");
  return { kind: "ttf", name, bytes, fallback: true, substituted: false };
}
